import { Component, effect, inject, viewChild } from '@angular/core';
import {
  ButtonComponent,
  CheckboxComponent,
  InputComponent,
  InputContainerComponent,
  NewsletterSubscribeComponent,
  ToastService,
  EditableTextDirective
} from "@components";
import { FileUploaderComponent } from "../../../components/forms/file-uploader/file-uploader.component";
import { MatExpansionPanel, MatExpansionPanelHeader, MatExpansionPanelTitle } from "@angular/material/expansion";
import { AbstractControl, FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from "@angular/forms";
import { AuthService } from "@api-services";
import { SignupDto } from "@api-contracts";
import { catchError, map, switchMap } from "rxjs/operators";
import { environment } from "../../../../environments/environment";
import { finalize, Observable, throwError } from "rxjs";
import { Router, RouterLink } from "@angular/router";
import { TranslocoPipe } from "@jsverse/transloco";
import { LayoutService } from "@services";
import { watchQueryParam } from '@utils';

export abstract class BaseSignupPageComponent {

  protected abstract readonly toastService: ToastService;
  protected abstract readonly layoutService: LayoutService;
  protected abstract readonly router: Router;
  protected abstract readonly authService: AuthService;

  abstract signupForm: FormGroup;

  passwordMatchValidator(formGroup: AbstractControl) {
    const password = formGroup.get('password')?.value;
    const confirmPassword = formGroup.get('confirmPassword')?.value;

    if (password !== confirmPassword) {
      formGroup.get('confirmPassword')?.setErrors({ passwordMismatch: true });
      return { passwordMismatch: true };
    } else {
      const errors = formGroup.get('confirmPassword')?.errors;
      if (errors && errors['passwordMismatch']) {
        delete errors['passwordMismatch'];
        formGroup.get('confirmPassword')?.setErrors(Object.keys(errors).length > 0 ? errors : null);
      }
      return null;
    }
  }

  abstract validate(): boolean;

  abstract preSignup(): Observable<(signupDto: SignupDto) => void>;

  validatePdf(fileUploader: FileUploaderComponent) {

    if (!fileUploader.valid()) {
      this.toastService.addMessage({
        severity: 'error',
        title: 'signup.pdfRequired'
      })
      return false;
    }

    // Verifico che il file sia un PDF
    const currentMedia = fileUploader.currentMedia();
    if (currentMedia?.file && currentMedia.file.type !== 'application/pdf') {
      this.toastService.addMessage({
        severity: 'error',
        title: 'signup.pdfInvalidFormat'
      })
      return false;
    }
    return true;
  }

  basePreSignup(fileUploader: FileUploaderComponent): Observable<(signupDto: SignupDto) => void> {
    return fileUploader.upload(x => {
      return x;
    }, undefined, true).pipe(
      map(upload => {
        return (signupDto: SignupDto) => {
          signupDto.cv = upload.data.id;
        }
      })
    );
  }

  signup() {
    const formValue = { ...this.signupForm.value, vatNumber: '' } as SignupDto & { confirmPassword: string, assignedToGallery: string | null };
    formValue.assignedToGallery = this.signupForm.controls['assignedToGallery']?.value;

    this.signupForm.markAllAsTouched();

    if (formValue?.password !== formValue?.confirmPassword) {
      this.toastService.addMessage({
        severity: 'error',
        title: 'signup.passwordNotMatch'
      })
      return;
    }

    if (!this.signupForm.valid) {
      this.toastService.addMessage({
        severity: 'error',
        title: 'signup.fillAllInputs'
      })
      return;
    }

    const valid = this.validate();
    if (!valid) {
      return;
    }

    if (this.signupForm.valid) {
      this.preSignup().pipe(
        switchMap(transform => {
          delete (formValue as any).confirmPassword; // Rimuovo confirmPassword prima di inviare
          transform(formValue)
          this.layoutService.increment()
          return this.authService.authsSignup({
            signupDto: formValue as SignupDto
          }, 'response')
        })
      )
        .pipe(catchError((err) => {
          if (err.status === 409) {
            this.toastService.addMessage({
              severity: 'error',
              title: 'signup.emailAlreadyUsed'
            })
          }
          return throwError(() => err)
        }),
          finalize(() => {
            this.layoutService.decrement();
          })).subscribe((f) => {
            debugger;
            this.toastService.addMessage({
              severity: 'success',
              title: 'signup.registrationSuccess',
            })
            this.signupForm.reset()
            this.router.navigate(['/'])
          })
    }
  }

  scrollToElement($element: HTMLDivElement): void {
    $element.scrollIntoView({ behavior: "smooth", block: "start", inline: "nearest" });
  }
}


@Component({
  selector: 'wun-artist-home-page',
  standalone: true,
  imports: [
    ButtonComponent,
    InputContainerComponent,
    InputComponent,
    FileUploaderComponent,
    CheckboxComponent,
    MatExpansionPanel,
    MatExpansionPanelHeader,
    MatExpansionPanelTitle,
    NewsletterSubscribeComponent,
    FormsModule,
    ReactiveFormsModule,
    TranslocoPipe,
    RouterLink,
    EditableTextDirective
],
  templateUrl: './artist-home-page.component.html',
  styleUrls: ['./artist-home-page.component.scss', '../base-user-page/base-user-page.component.scss']
})
export class ArtistHomePageComponent extends BaseSignupPageComponent {

  code = watchQueryParam('code');
  layoutService = inject(LayoutService);

  authService = inject(AuthService);
  toastService = inject(ToastService);
  router = inject(Router);

  // Fallback texts for editable translations
  readonly fallbacks = {
    header: {
      title: 'Grow your art career with Wundart',
      subtitle: 'Share, sell, showcase your art with the world'
    },
    joinButton: 'Join Now',
    trial: {
      offer: 'Limited-time offer',
      days: 'Get your 90 days free trial',
      tryButton: 'Try Wundart'
    },
    whyJoin: {
      title: 'Why join Wundart?',
      features: [
        {
          title: 'Reach a wider audience',
          content: 'Get visibility through Wundart\'s website, social media campaigns, newsletters, and our curated collector network.'
        },
        {
          title: 'Sell without commission',
          content: 'Keep 60% of your artwork\'s price — we only charge a subscription fee, and shipping costs are covered by the buyer.'
        },
        {
          title: 'Not just selling, but exhibiting',
          content: 'You could have the possibility to showcase your artworks in our associated galleries\' exhibitions, gaining exposure beyond the digital platform.'
        },
        {
          title: 'Secure payments',
          content: 'Receive your payment safely via PayPal or bank transfer, 14 business days after the artwork is delivered to the buyer.'
        },
        {
          title: 'Artwork protection',
          content: 'Every piece comes with a certificate of authenticity. If you already have one, that\'s great — otherwise, we provide it for you.'
        },
        {
          title: 'Fair artwork pricing',
          content: 'We will help you sell your works at the right price. If your artworks haven\'t been evaluated before, our team of experts will assess them accurately and transparently.'
        },
        {
          title: 'Dedicated art advisor support',
          content: 'Our Art Advisors engage directly with collectors on your behalf, helping you process sales and ensuring smooth transactions.'
        },
        {
          title: 'Safe and trusted shipping',
          content: 'Partnering with professional art shippers, we ensure your artworks are packaged, insured, and delivered safely worldwide.'
        }
      ]
    },
    questions: {
      title: 'Have more questions?',
      faqButton: 'Check out our FAQ',
      exchangePolicy: 'Exchange & Refund Policy',
      artistExperience: 'Artist Experience'
    },
    plans: {
      title: 'Our plans for you',
      monthly: {
        title: 'Monthly Plan',
        price: '20 €',
        goldMembership: 'Gold Membership',
        goldPrice: '40 €'
      },
      trimester: {
        title: 'Trimester Plan',
        price: '54 €',
        goldMembership: 'Gold Membership',
        goldPrice: '108 €'
      },
      annual: {
        title: 'Annual Plan',
        price: '204 €',
        goldMembership: 'Gold Membership',
        goldPrice: '408 €'
      }
    },
    steps: {
      offer: 'Limited-time offer',
      trial: 'Start your 90-day free trial',
      apply: {
        title: 'APPLY',
        description: 'Tell us about yourself and submit your artwork using the application form.'
      },
      approved: {
        title: 'GET APPROVED',
        description: 'Once approved, your free trial begins and you can start selling immediately.'
      },
      days: {
        title: '90 DAYS FREE',
        description: 'Enjoy full access to all features. Choose a plan or cancel anytime.'
      },
      tryButton: 'Try Wundart'
    },
    applyNow: {
      title: 'Apply now',
      description: 'Send your application to our Admissions Committee. We will review it and get back to you soon.',
      descriptionLine2: 'Once accepted, you can choose the subscription plan that suits you best.'
    },
    form: {
      name: 'Name',
      surname: 'Surname',
      email: 'Email',
      phone: 'Phone number',
      country: 'Country',
      city: 'City',
      youAre: 'You are',
      beginner: 'Beginner artist',
      established: 'Established artist',
      socialMediaLabel: 'Share a website or social media link showcasing your art',
      uploadPortfolio: 'Upload your PDF portfolio',
      selectFile: 'Select file',
      galleriesLabel: 'Galleries you have collaborated with (if you are an emerging artist at your first experience, write \'none\')',
      exhibitionsLabel: 'List of exhibitions and art fairs (if you are an emerging artist at your first experience, write \'none\')',
      nickname: 'Nickname',
      galleryCodeLabel: 'Do you have a gallery representing you? if you have it please enter the provider code you received from your gallery',
      galleryCodePlaceholder: 'Gallery provider code',
      password: 'Your password',
      confirmPassword: 'Confirm your password',
      termsPrefix: 'By registering, you agree to our',
      termsLink: 'Terms of Service',
      termsAnd: 'and',
      privacyLink: 'Privacy Policy',
      send: 'Send'
    },
    faq: {
      q1: {
        question: 'HOW DOES WUNDART WORK?',
        intro: 'Wundart is an up and coming reality grounded in quality and an immense love for anything creative where you, as an artist, are the focus.'
      },
      q2: {
        question: 'HOW DOES THE SELECTION PROCESS WORK?',
        title: 'The Wundart Admissions Committee',
        content: 'At the heart of Wundart\'s excellence is our Admissions Committee, a panel of distinguished art professionals and industry experts dedicated to upholding the platform\'s high standards. The members of the committee are qualified gallerists and curators from established realities Young Art Hunters and Almach Art Gallery. Each application is carefully evaluated for artistic quality, originality, and international potential.',
        content2: 'Through this selective and thoughtful process, we ensure that only the most exceptional and promising artists are invited to join Wundart — gaining the opportunity to showcase their work to a global audience and thrive in the international art market.'
      },
      q3: {
        question: 'IS BEING REPRESENTED BY WUNDART FREE?',
        content: 'Applying to Wundart and having your portfolio reviewed by our Admissions Committee is completely free.',
        content2: 'If selected, you\'ll have the opportunity to choose from our paid subscription plans, available with monthly or yearly billing.',
        content3: 'Artists without prior sales history will be asked to submit their artworks to the Committee for quotation — ensuring fair and accurate pricing for your pieces.'
      },
      q4: {
        question: 'HOW MANY ARTWORKS CAN BE ON MY PROFILE?',
        silverPlan: 'Silver Plan',
        silverContent: 'Showcase up to 10 artworks on your profile at a time — and feel free to update or replace them whenever you wish.',
        goldPlan: 'Gold Plan',
        goldContent: 'Enjoy unlimited uploads and display your entire portfolio online, offering collectors a complete and evolving view of your artistic universe.'
      },
      q5: {
        question: 'WHAT KINDS OF MEDIUM ARE ACCEPTED?',
        content: 'We welcome a wide range of artistic mediums — from paintings and photographs to limited edition prints, digital artworks, and works on paper or textile.',
        content2: 'At the moment, we\'re unable to accept artworks made of glass, ceramics, jewelry, or clothing.',
        content3: 'Coming soon: sculpture submissions will also be available!'
      },
      q6: {
        question: 'HOW WILL WUNDART PROVIDE VISIBILITY FOR MY ART?',
        content: 'At Wundart, we\'re committed to helping artists gain meaningful exposure and grow their careers. Our constantly evolving marketing strategy ensures your artworks reach the right audience through multiple channels — including web search, social media, email campaigns, and more.',
        content2: 'Our marketing investments span paid advertising, SEO optimization, strategic partnerships, and press coverage, all designed to maximize visibility and sales potential.',
        content3: 'In addition, our Art Advisor team, personal collectors database, and smart recommendation algorithm work together to connect your art with collectors who share your aesthetic and interests — giving your work the attention it truly deserves.'
      },
      q7: {
        question: 'CAN I STILL COLLABORATE WITH OTHER GALLERIES/PORTALS?',
        content: 'Absolutely! At Wundart, we believe your art should reach the world in every way possible. You\'re free to collaborate with other galleries, exhibitions, and online platforms while showcasing your work with us.',
        content2: 'To ensure clarity and visibility, any artworks not currently represented by another gallery will be featured exclusively on Wundart — giving them a dedicated spotlight within our global community.',
        content3: 'Meanwhile, artworks already represented elsewhere remain non-exclusive, so you can continue sharing them freely.'
      },
      q8: {
        question: 'WHO SETS THE PRICE OF MY ARTWORKS?',
        content: 'Artists that provide a curriculum of previous experiences and sales will have full control over the prices of their artworks. However, totally emerging artists must submit their works for an official quotation process by our qualified Curatorial Team.'
      },
      q9: {
        question: 'DOES WUNDART ACCEPT ARTISTS FROM EVERY COUNTRY?',
        content: 'At the moment Wundart can only accept artists based in the European Union, Switzerland and United Kingdom, however we plan to expand Wundart\'s reach to many more countries and eventually globally in the near future.'
      }
    }
  };

  fileUploader = viewChild.required<FileUploaderComponent>(FileUploaderComponent);

  signupForm = new FormGroup({
    username: new FormControl('', [Validators.required]),
    password: new FormControl('', [Validators.required]),
    confirmPassword: new FormControl('', [Validators.required]),
    firstName: new FormControl('', [Validators.required]),
    lastName: new FormControl('', [Validators.required]),
    email: new FormControl('', [Validators.required, Validators.email]),
    city: new FormControl('', [Validators.required]),
    country: new FormControl('', [Validators.required]),
    phone: new FormControl('', [Validators.required]),
    profileType: new FormControl<'ARTIST' | 'COLLECTOR'>('ARTIST', [Validators.required]),
    businessName: new FormControl<string | null>(null),
    exhibitions: new FormControl<string | null>('none'),
    termsAndConditions: new FormControl(false, [Validators.requiredTrue]),
    socialMediaLinks: new FormControl<string | null>(null),
    galleries: new FormControl('', [Validators.required]),
    typeOfArtist: new FormControl('', Validators.required),
    assignedToGallery: new FormControl<string | null>(null)
  }, {
    validators: [
      (form) => this.passwordMatchValidator(form)
    ]
  });

  constructor() {
    super();
    if (environment.env === 'local') {
      this.signupForm.setValue({
        username: 'artist1',
        password: 'Password123!',
        confirmPassword: 'Password123!',
        city: 'Rome',
        country: 'Italy',
        email: 'simonedisante@gmail.com',
        firstName: 'Simone',
        lastName: 'Disante',
        phone: '+391234567890',
        profileType: 'ARTIST',
        businessName: null,
        exhibitions: 'none',
        termsAndConditions: true,
        socialMediaLinks: 'https://instagram.com/simonedisante',
        galleries: 'none',
        typeOfArtist: 'none',
        assignedToGallery: null
      })
    }

    effect(() => {
      const code = this.code();
      if (code) {
        this.signupForm.controls['assignedToGallery'].setValue(code);
        this.signupForm.controls['assignedToGallery'].disable();
      }
    })
  }

  override validate() {
    return this.validatePdf(this.fileUploader())
  }

  preSignup(): Observable<(signupDto: SignupDto) => void> {
    return this.basePreSignup(this.fileUploader());
  }
}
